import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import { Tooltip } from './ui/Tooltip';
import tableTemplatesService, { type TableLayoutTemplate } from '../services/tableTemplates.service';

/** Pull a human message off an axios-ish error without leaking `[object Object]`. */
const errorMessage = (err: unknown, fallback: string): string => {
  const detail = (err as any)?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  const message = (err as any)?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
};

const statusOf = (err: unknown): number | undefined => (err as any)?.response?.status;

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

/**
 * Which kind of grid this layout was saved from — "Table" or "Multi Row" — read
 * from the `layoutMode` that buildTemplateSnapshot stamps into the blob.
 *
 * Needed because every LC/Dispatch custom card pools its layouts under ONE
 * settings_key, so the key can no longer distinguish the two the way `lc-card-`
 * vs `lc-cards-` used to. The two render from different column sets, so loading a
 * Multi Row layout into a table grid applies as a mostly-empty layout; labelling
 * is what lets a user avoid that. Returns '' for rows saved before the stamp
 * existed, and the caller then shows no label rather than guessing wrong.
 */
export const layoutModeLabel = (t: TableLayoutTemplate): string => {
  const mode = (t.setting_value as { layoutMode?: unknown } | null | undefined)?.layoutMode;
  if (mode === 'cards') return 'Multi Row';
  if (mode === 'table') return 'Table';
  return '';
};

/** ` · Multi Row` when known, nothing when not — for a row's caption line. */
const modeSuffix = (t: TableLayoutTemplate): string => {
  const label = layoutModeLabel(t);
  return label ? ` · ${label}` : '';
};

const OVERLAY_CLASS =
  'fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-[9999] p-4';

const PRIMARY_BUTTON_CLASS =
  'rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-50';

const SECONDARY_BUTTON_CLASS =
  'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-50';

const TEXT_INPUT_CLASS =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-heading placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400';

const DANGER_BUTTON_CLASS =
  'rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:cursor-not-allowed disabled:opacity-50';

const DANGER_OUTLINE_BUTTON_CLASS =
  'rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700/60 dark:text-red-400 dark:hover:bg-red-900/20';

const ROW_CLASS = 'flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-900';

// ---------------------------------------------------------------------------
// Shared row machinery — Share / Rename / Delete with a per-row lock
//
// Used by ManageViewsModal (both of its sections) and SaveViewModal, so the
// locking, the rename-collision handling and the two-click delete behave the
// same everywhere and are tested once. LoadTemplateModal is load-only and does
// not use it.
// ---------------------------------------------------------------------------

/** The user-facing noun for a row: is_view=1 is a "System View", else a "View". */
const nounOf = (row: TableLayoutTemplate): 'View' | 'System View' =>
  row.is_view ? 'System View' : 'View';

interface RowMutationsOptions {
  /** Patch one row wherever it lives — ids are unique across every list (one table). */
  updateRow: (id: number, patch: Partial<TableLayoutTemplate>) => void;
  removeRow: (id: number) => void;
  setError: (message: string | null) => void;
  /** Resync from the server after a failed optimistic write. Optional. */
  refresh?: () => void;
  /** Fires the moment a write begins, so the host can invalidate an in-flight refetch. */
  onMutationStart?: () => void;
}

export interface RowMutations {
  busyIds: ReadonlySet<number>;
  renamingId: number | null;
  renameValue: string;
  setRenameValue: (value: string) => void;
  renameError: string | null;
  confirmDeleteId: number | null;
  /** Clear every transient (rename editor, armed delete, locks) — call on open. */
  reset: () => void;
  toggleShare: (row: TableLayoutTemplate) => Promise<void>;
  startRename: (row: TableLayoutTemplate) => void;
  cancelRename: () => void;
  commitRename: (row: TableLayoutTemplate) => Promise<void>;
  handleDelete: (row: TableLayoutTemplate) => Promise<void>;
}

function useRowMutations({
  updateRow,
  removeRow,
  setError,
  refresh,
  onMutationStart,
}: RowMutationsOptions): RowMutations {
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  /**
   * Ids with a mutation in flight. Locking is PER ROW — a request on one row
   * must never make another row's buttons dead. The ref is the gate (claimed
   * synchronously, so two clicks on the same row in one tick cannot both pass);
   * the state is its render twin, for disabling that row.
   */
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(() => new Set());
  const busyIdsRef = useRef<ReadonlySet<number>>(new Set());

  // Latest callbacks without re-creating every handler when the host re-renders.
  const optsRef = useRef({ updateRow, removeRow, setError, refresh, onMutationStart });
  optsRef.current = { updateRow, removeRow, setError, refresh, onMutationStart };

  const reset = useCallback(() => {
    setRenamingId(null);
    setRenameError(null);
    setConfirmDeleteId(null);
    busyIdsRef.current = new Set();
    setBusyIds(new Set());
  }, []);

  /** Claim this row's lock, or refuse if the same row is already mutating. */
  const beginMutation = useCallback((id: number): boolean => {
    if (busyIdsRef.current.has(id)) return false;
    const next = new Set(busyIdsRef.current);
    next.add(id);
    busyIdsRef.current = next;
    optsRef.current.onMutationStart?.();
    setBusyIds(next);
    return true;
  }, []);

  const endMutation = useCallback((id: number) => {
    const next = new Set(busyIdsRef.current);
    next.delete(id);
    busyIdsRef.current = next;
    setBusyIds(next);
  }, []);

  const toggleShare = useCallback(
    async (row: TableLayoutTemplate) => {
      if (!beginMutation(row.id)) return;
      setConfirmDeleteId(null);
      const next = !row.is_shared;
      // Optimistic — the toggle should not lag a round-trip.
      optsRef.current.updateRow(row.id, { is_shared: next });
      optsRef.current.setError(null);
      try {
        const updated = await tableTemplatesService.update(row.id, { is_shared: next });
        // Trust the server's value over the optimistic guess.
        const applied = typeof updated?.is_shared === 'boolean' ? updated.is_shared : next;
        optsRef.current.updateRow(row.id, { is_shared: applied });
      } catch (err) {
        optsRef.current.setError(errorMessage(err, 'Could not change sharing.'));
        // Resync from the server. Safe to fire and forget when the host guards
        // its refetch by generation.
        optsRef.current.refresh?.();
      } finally {
        endMutation(row.id);
      }
    },
    [beginMutation, endMutation],
  );

  const startRename = useCallback((row: TableLayoutTemplate) => {
    setConfirmDeleteId(null);
    setRenamingId(row.id);
    setRenameValue(row.name);
    setRenameError(null);
  }, []);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameError(null);
  }, []);

  const commitRename = useCallback(
    async (row: TableLayoutTemplate) => {
      const next = renameValue.trim();
      if (!next || next === row.name) {
        cancelRename();
        return;
      }
      // Also the Enter-then-blur interlock: the second commit finds the slot taken.
      if (!beginMutation(row.id)) return;
      setRenameError(null);
      try {
        const updated = await tableTemplatesService.update(row.id, { name: next });
        optsRef.current.updateRow(row.id, { name: updated?.name ?? next });
        setRenamingId(null);
      } catch (err) {
        // 409 = a row with that name already exists. Keep the input open so the
        // name can be fixed in place.
        setRenameError(
          statusOf(err) === 409
            ? `A ${nounOf(row)} with that name already exists.`
            : errorMessage(err, `Could not rename the ${nounOf(row)}.`),
        );
      } finally {
        endMutation(row.id);
      }
    },
    [beginMutation, cancelRename, endMutation, renameValue],
  );

  const handleDelete = useCallback(
    async (row: TableLayoutTemplate) => {
      // Arming the confirm is local state only — it never consults in-flight work.
      if (confirmDeleteId !== row.id) {
        setConfirmDeleteId(row.id);
        return;
      }
      if (!beginMutation(row.id)) return;
      setConfirmDeleteId(null);
      optsRef.current.setError(null);
      try {
        await tableTemplatesService.remove(row.id);
        optsRef.current.removeRow(row.id);
      } catch (err) {
        optsRef.current.setError(errorMessage(err, `Could not delete the ${nounOf(row)}.`));
      } finally {
        endMutation(row.id);
      }
    },
    [beginMutation, confirmDeleteId, endMutation],
  );

  return {
    busyIds,
    renamingId,
    renameValue,
    setRenameValue,
    renameError,
    confirmDeleteId,
    reset,
    toggleShare,
    startRename,
    cancelRename,
    commitRename,
    handleDelete,
  };
}

interface LayoutRowProps {
  row: TableLayoutTemplate;
  /** The small line under the name (owner, date, layout mode). */
  caption: React.ReactNode;
  /** Rename/Delete (+ Share when `showShare`). Omit for a read-only row. */
  mutations?: RowMutations;
  showShare?: boolean;
  /** Rendered ahead of the management controls — e.g. a Load button. */
  leading?: React.ReactNode;
}

/** One list row: name + caption, or an inline rename editor, then its buttons. */
const LayoutRow: React.FC<LayoutRowProps> = ({ row, caption, mutations, showShare, leading }) => {
  const noun = nounOf(row);
  const renaming = mutations?.renamingId === row.id;
  const confirming = mutations?.confirmDeleteId === row.id;
  const busy = mutations?.busyIds.has(row.id) ?? false;
  return (
    <li className={ROW_CLASS}>
      <div className="min-w-0 flex-1">
        {renaming && mutations ? (
          <>
            <input
              type="text"
              autoFocus
              maxLength={100}
              aria-label={`New ${noun} name`}
              value={mutations.renameValue}
              onChange={(e) => mutations.setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void mutations.commitRename(row);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  mutations.cancelRename();
                }
              }}
              onBlur={() => void mutations.commitRename(row)}
              className={TEXT_INPUT_CLASS}
            />
            {mutations.renameError && (
              <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
                {mutations.renameError}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="truncate font-medium text-heading">{row.name}</p>
            <p className="truncate text-xs text-muted">{caption}</p>
          </>
        )}
      </div>

      {(leading || mutations) && (
        <div className="flex shrink-0 items-center gap-2">
          {leading}
          {mutations && showShare && (
            <button
              type="button"
              onClick={() => void mutations.toggleShare(row)}
              disabled={busy}
              aria-label={
                row.is_shared
                  ? `Stop sharing ${noun} "${row.name}"`
                  : `Share ${noun} "${row.name}" with everyone`
              }
              className={SECONDARY_BUTTON_CLASS}
            >
              {row.is_shared ? 'Shared' : 'Share'}
            </button>
          )}
          {mutations && !renaming && (
            <button
              type="button"
              onClick={() => mutations.startRename(row)}
              disabled={busy}
              aria-label={`Rename ${noun} "${row.name}"`}
              className={SECONDARY_BUTTON_CLASS}
            >
              Rename
            </button>
          )}
          {mutations && (
            <button
              type="button"
              onClick={() => void mutations.handleDelete(row)}
              disabled={busy}
              aria-label={
                confirming
                  ? `Confirm delete of ${noun} "${row.name}"`
                  : `Delete ${noun} "${row.name}"`
              }
              className={confirming ? DANGER_BUTTON_CLASS : DANGER_OUTLINE_BUTTON_CLASS}
            >
              {confirming ? 'Confirm?' : 'Delete'}
            </button>
          )}
        </div>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// Save  — UI: "Save As View" (a personal layout, is_view=0)
//
// TERMINOLOGY for this whole file: the UI calls an is_view=0 row a "View" and an
// is_view=1 row a "System View". Every `template` identifier below is the former.
// The DB column, the endpoints and the component id keep their original names.
// ---------------------------------------------------------------------------

export interface SaveTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  settingsKey: string;
  /** Called at save time — snapshots the CURRENT visible layout (incl. staged edit-mode changes). */
  buildSnapshot: () => any;
}

export const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  isOpen,
  onClose,
  settingsKey,
  buildSnapshot,
}) => {
  // Every hook lives above the `if (!isOpen)` bail-out — a hook below a
  // conditional return throws React #310 and blanks the whole page.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  const [name, setName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [mine, setMine] = useState<TableLayoutTemplate[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fresh state + a fresh name list on every open.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setName('');
    setIsShared(false);
    setError(null);
    setSaving(false);
    setMine([]);
    tableTemplatesService
      .list(settingsKey)
      .then((rows) => {
        if (!cancelled) setMine(rows.filter((r) => r.is_mine));
      })
      .catch(() => {
        // A failed lookup only costs the overwrite hint; saving still works.
        if (!cancelled) setMine([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, settingsKey]);

  const trimmed = name.trim();

  const match = useMemo(() => {
    if (!trimmed) return null;
    const lowered = trimmed.toLowerCase();
    return mine.find((t) => t.name.trim().toLowerCase() === lowered) ?? null;
  }, [mine, trimmed]);

  // Landing on an existing name adopts that template's current sharing, but the
  // user stays free to change the checkbox afterwards — so this only fires when
  // the matched template actually changes.
  const lastMatchIdRef = useRef<number | null>(null);
  useEffect(() => {
    const id = match ? match.id : null;
    if (id === lastMatchIdRef.current) return;
    lastMatchIdRef.current = id;
    if (match) setIsShared(match.is_shared);
  }, [match]);

  const handleSave = useCallback(async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      await tableTemplatesService.save(settingsKey, trimmed, isShared, buildSnapshot());
      setSaving(false);
      onClose();
    } catch (err) {
      setSaving(false);
      setError(errorMessage(err, 'Could not save the View.'));
    }
  }, [buildSnapshot, isShared, onClose, saving, settingsKey, trimmed]);

  if (!isOpen) return null;

  return (
    <div className={OVERLAY_CLASS}>
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Save As View"
        className="bg-panel rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <ModalHeader title="Save As View" onClose={onClose} dragHandleProps={dragHandleProps} />

        <div className="space-y-4 p-6">
          <div>
            <label
              htmlFor="table-template-name"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              View name
            </label>
            <input
              id="table-template-name"
              type="text"
              autoFocus
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSave();
                }
              }}
              placeholder="e.g. Dispatch View"
              className={TEXT_INPUT_CLASS}
            />
          </div>

          {match && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
              Will update your existing View &ldquo;{match.name}&rdquo;
            </p>
          )}

          <div className="flex items-center gap-2">
            <input
              id="table-template-shared"
              type="checkbox"
              checked={isShared}
              onChange={(e) => setIsShared(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-400 dark:border-gray-600"
            />
            <label
              htmlFor="table-template-shared"
              className="text-sm text-gray-700 dark:text-gray-200"
            >
              Share with everyone
            </label>
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!trimmed || saving}
              className={PRIMARY_BUTTON_CLASS}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Load  — UI: "Load View" (own + others' shared personal layouts)
// ---------------------------------------------------------------------------

export interface LoadTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Origin key for the request route. The loader lists accessible Views across
   * all table keys; saving still uses the originating table or pooled key.
   */
  settingsKey: string;
  /** Apply the chosen snapshot to the table (TanTable wires this). */
  onLoad: (snapshot: any) => void;
  /** Destination-specific column/access validation; also checked on click. */
  getUnavailableReason?: (snapshot: unknown) => string | undefined;
}

export const LoadTemplateModal: React.FC<LoadTemplateModalProps> = ({
  isOpen,
  onClose,
  settingsKey,
  onLoad,
  getUnavailableReason,
}) => {
  // All hooks above the `if (!isOpen)` bail-out (React #310).
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  const [templates, setTemplates] = useState<TableLayoutTemplate[]>([]);
  /**
   * System Views, app-wide: every is_view=1 row from EVERY settings key, so a
   * manager publishes once and it is reachable from any grid showing this modal.
   * Kept in its own state because it is a separate request on a separate scope —
   * folding it into `templates` would put it under the pool's generation guard and
   * make a System View look like one of the caller's own rows.
   */
  const [systemViews, setSystemViews] = useState<TableLayoutTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Bumped by every refresh. A `list()` in flight captures the value and throws
   * its result away if the counter moved meanwhile (a reopen on another key).
   */
  const refreshGenRef = useRef(0);

  const refresh = useCallback(async () => {
    const gen = ++refreshGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const rows = await tableTemplatesService.list(settingsKey, true);
      if (gen === refreshGenRef.current) setTemplates(rows);
    } catch (err) {
      if (gen === refreshGenRef.current) {
        setTemplates([]);
        setError(errorMessage(err, 'Could not load Views.'));
      }
    } finally {
      if (gen === refreshGenRef.current) setLoading(false);
    }
  }, [settingsKey]);

  useEffect(() => {
    if (!isOpen) return;
    void refresh();
  }, [isOpen, refresh]);

  // System Views, fetched separately and independently: they are read-only here,
  // never mutated, and losing them must not blank the caller's own rows (or the
  // reverse). A failure leaves the section empty rather than surfacing an error —
  // the caller's own Views are still perfectly usable without it.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSystemViews([]);
    tableTemplatesService
      .listAllViews()
      .then((rows) => {
        if (!cancelled) setSystemViews(rows);
      })
      .catch(() => {
        if (!cancelled) setSystemViews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const { mine, shared } = useMemo(
    () => ({
      mine: templates.filter((t) => t.is_mine),
      shared: templates.filter((t) => !t.is_mine),
    }),
    [templates],
  );

  const handleLoad = useCallback(
    (template: TableLayoutTemplate) => {
      if (getUnavailableReason?.(template.setting_value)) return;
      onLoad(template.setting_value);
      onClose();
    },
    [onClose, onLoad, getUnavailableReason],
  );

  if (!isOpen) return null;

  const renderLoadButton = (template: TableLayoutTemplate) => {
    const reason = getUnavailableReason?.(template.setting_value);
    const button = (
      <button
        type="button"
        onClick={() => handleLoad(template)}
        disabled={!!reason}
        aria-label={`Load ${template.is_view ? 'System View' : 'View'} "${template.name}"`}
        className={`${PRIMARY_BUTTON_CLASS}${reason ? ' pointer-events-none border-2 border-red-500 line-through decoration-red-500 decoration-2' : ''}`}
      >
        Load
      </button>
    );
    return reason ? (
      <Tooltip content={reason} delay={0} zIndex={10000}>
        <span tabIndex={0} aria-label={reason} className="inline-flex cursor-not-allowed">
          {button}
        </span>
      </Tooltip>
    ) : button;
  };

  // Every row is LOAD-ONLY, including the caller's own: Share / Rename / Delete
  // live in Manage Views (and, for System Views, also in Save As System View).
  const renderMineRow = (template: TableLayoutTemplate) => (
    <LayoutRow
      key={template.id}
      row={template}
      caption={<>Updated {formatDate(template.updated_at)}{modeSuffix(template)}</>}
      leading={renderLoadButton(template)}
    />
  );

  const renderSharedRow = (template: TableLayoutTemplate) => (
    <LayoutRow
      key={template.id}
      row={template}
      caption={<>{template.user_email} &middot; {formatDate(template.updated_at)}{modeSuffix(template)}</>}
      leading={renderLoadButton(template)}
    />
  );

  const renderSystemViewRow = (view: TableLayoutTemplate) => (
    <LayoutRow
      key={`sys-${view.id}`}
      row={view}
      caption={<>{view.user_email} &middot; {formatDate(view.updated_at)}{modeSuffix(view)}</>}
      leading={renderLoadButton(view)}
    />
  );

  return (
    <div className={OVERLAY_CLASS}>
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Load View"
        className="bg-panel rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <ModalHeader title="Load View" onClose={onClose} dragHandleProps={dragHandleProps} />

        <div className="space-y-6 p-6">
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          {/* System Views sit above the caller's own, matching the order the
              Custom Search / Custom Filter picker uses. Rendered outside the
              `loading` gate because they load on their own request — a slow pool
              fetch should not hide them, and vice versa. Hidden entirely when none
              are published, rather than showing an empty heading. */}
          {systemViews.length > 0 && (
            <section>
              <h3 className="mb-3 text-lg font-semibold text-heading">
                System Views
              </h3>
              <ul className="space-y-2">{systemViews.map(renderSystemViewRow)}</ul>
            </section>
          )}

          {loading ? (
            <p className="py-8 text-center text-gray-600 dark:text-gray-400">Loading Views...</p>
          ) : (
            <>
              <section>
                <h3 className="mb-3 text-lg font-semibold text-heading">
                  My Views
                </h3>
                {mine.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    You have not saved any Views for this table yet.
                  </p>
                ) : (
                  <ul className="space-y-2">{mine.map(renderMineRow)}</ul>
                )}
              </section>

              <section>
                <h3 className="mb-3 text-lg font-semibold text-heading">Shared</h3>
                {shared.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No one has shared a View for this table.
                  </p>
                ) : (
                  <ul className="space-y-2">{shared.map(renderSharedRow)}</ul>
                )}
              </section>
            </>
          )}

          <div className="flex justify-end pt-2">
            <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Save As System View (manager-only)
// ---------------------------------------------------------------------------

export interface SaveViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  settingsKey: string;
  /** Called at save time — snapshots the CURRENT visible layout (incl. staged edit-mode changes). */
  buildSnapshot: () => any;
}

/**
 * Publish the current layout as a named SYSTEM VIEW, and manage the ones already
 * published. Unlike SaveTemplateModal there is no sharing checkbox — a System View
 * is org-wide by definition (the endpoint forces is_shared on) — and the existing
 * ones are listed inline so a manager can see what is live, overwrite one by name,
 * rename it or retire it without a second modal.
 *
 * Rename/Delete follow the server's `can_manage` flag rather than ownership: any
 * holder of the save-search-view permission may edit any System View, so one
 * published by someone who has since left never becomes stranded.
 *
 * TERMINOLOGY: this modal's rows are is_view=1 ("System View" in the UI).
 * SaveTemplateModal / LoadTemplateModal above handle is_view=0, which the UI calls
 * plain "View". The code keeps the original template/view identifiers.
 */
export const SaveViewModal: React.FC<SaveViewModalProps> = ({
  isOpen,
  onClose,
  settingsKey,
  buildSnapshot,
}) => {
  // Every hook lives above the `if (!isOpen)` bail-out — a hook below a
  // conditional return throws React #310 and blanks the whole page.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  const [name, setName] = useState('');
  const [views, setViews] = useState<TableLayoutTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRow = useCallback((id: number, patch: Partial<TableLayoutTemplate>) => {
    setViews((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  }, []);
  const removeRow = useCallback((id: number) => {
    setViews((prev) => prev.filter((v) => v.id !== id));
  }, []);
  const mutations = useRowMutations({ updateRow, removeRow, setError });
  const resetMutations = mutations.reset;

  const refresh = useCallback(
    async (showLoading = false) => {
      if (showLoading) setLoading(true);
      try {
        setViews(await tableTemplatesService.listViews(settingsKey));
      } catch (err) {
        // Only the opening fetch may blank the list; a corrective refetch keeps
        // stale rows rather than showing a misleading empty state.
        if (showLoading) setViews([]);
        setError(errorMessage(err, 'Could not load the published System Views.'));
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [settingsKey],
  );

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setError(null);
    setSaving(false);
    resetMutations();
    void refresh(true);
  }, [isOpen, refresh, resetMutations]);

  const trimmed = name.trim();

  // Views are keyed by name alone, so an existing name means overwrite — for
  // everyone, not just for this manager. Worth saying out loud before they save.
  const match = useMemo(() => {
    if (!trimmed) return null;
    const lowered = trimmed.toLowerCase();
    return views.find((v) => v.name.trim().toLowerCase() === lowered) ?? null;
  }, [views, trimmed]);

  const handleSave = useCallback(async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      await tableTemplatesService.saveView(settingsKey, trimmed, buildSnapshot());
      setSaving(false);
      onClose();
    } catch (err) {
      setSaving(false);
      setError(
        statusOf(err) === 403
          ? 'You do not have permission to publish System Views.'
          : errorMessage(err, 'Could not save the System View.'),
      );
    }
  }, [buildSnapshot, onClose, saving, settingsKey, trimmed]);

  if (!isOpen) return null;

  // Rename/Delete only where the server says the caller may; no Share — a
  // System View is org-wide by definition.
  const renderViewRow = (view: TableLayoutTemplate) => (
    <LayoutRow
      key={view.id}
      row={view}
      caption={<>Published by {view.user_email} &middot; {formatDate(view.updated_at)}</>}
      mutations={view.can_manage ? mutations : undefined}
    />
  );

  return (
    <div className={OVERLAY_CLASS}>
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Save As System View"
        className="bg-panel rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <ModalHeader title="Save As System View" onClose={onClose} dragHandleProps={dragHandleProps} />

        <div className="space-y-4 p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            A System View is published to everyone. Anyone running a Custom Search or Custom
            Filter can pick it to see their results in this layout.
          </p>

          <div>
            <label
              htmlFor="search-system-view-name"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              System View name
            </label>
            <input
              id="search-system-view-name"
              type="text"
              autoFocus
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSave();
                }
              }}
              placeholder="e.g. Ops Board"
              className={TEXT_INPUT_CLASS}
            />
          </div>

          {match && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
              Will replace the published System View &ldquo;{match.name}&rdquo; for everyone
            </p>
          )}

          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <section>
            <h3 className="mb-3 text-sm font-semibold text-heading">
              Published System Views
            </h3>
            {loading ? (
              <p className="py-4 text-center text-gray-600 dark:text-gray-400">Loading System Views...</p>
            ) : views.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No System Views have been published for this table yet.
              </p>
            ) : (
              <ul className="space-y-2">{views.map(renderViewRow)}</ul>
            )}
          </section>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!trimmed || saving}
              className={PRIMARY_BUTTON_CLASS}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Manage Views — UI: "Manage Views" (rename / share / delete, never load)
// ---------------------------------------------------------------------------

export interface ManageViewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Origin key for the request route; both listings are app-wide. */
  settingsKey: string;
}

/**
 * One place to tidy up: the caller's OWN Views (Share / Rename / Delete) and,
 * for a holder of the save-search-view permission, every published System View
 * (Rename / Delete). Other people's shared personal Views are not listed — there
 * is nothing the caller may do to them. Nothing here applies a layout to the
 * grid; that is Load View's job, which is load-only in return.
 *
 * Both lists come from the same requests Load View makes, so what you can load
 * there you can manage here. Management rows are filtered client-side on the
 * server's `is_mine` / `can_manage` flags — the server still enforces them.
 */
export const ManageViewsModal: React.FC<ManageViewsModalProps> = ({ isOpen, onClose, settingsKey }) => {
  // All hooks above the `if (!isOpen)` bail-out (React #310).
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  const [mine, setMine] = useState<TableLayoutTemplate[]>([]);
  const [systemViews, setSystemViews] = useState<TableLayoutTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Bumped by every refresh AND every mutation. A fetch in flight captures the
   * value and throws its result away if the counter moved meanwhile, so a slow
   * refetch can never overwrite newer state.
   */
  const refreshGenRef = useRef(0);

  /**
   * @param showLoading only the initial open swaps the lists for a spinner; a
   * corrective refetch must leave the rows on screen.
   */
  const refresh = useCallback(
    async (showLoading = false) => {
      const gen = ++refreshGenRef.current;
      if (showLoading) setLoading(true);
      setError(null);
      // Two independent requests: losing the System View list must not blank
      // the caller's own rows, or the reverse.
      const [own, sys] = await Promise.allSettled([
        tableTemplatesService.list(settingsKey, true),
        tableTemplatesService.listAllViews(),
      ]);
      if (showLoading) setLoading(false);
      if (gen !== refreshGenRef.current) return;
      if (own.status === 'fulfilled') {
        setMine(own.value.filter((r) => r.is_mine));
      } else {
        // Only the opening fetch may blank the list — for a background
        // corrective refetch, stale rows beat a misleading empty state.
        if (showLoading) setMine([]);
        setError(errorMessage(own.reason, 'Could not load your Views.'));
      }
      if (sys.status === 'fulfilled') {
        setSystemViews(sys.value.filter((r) => r.can_manage));
      } else if (showLoading) {
        setSystemViews([]);
      }
    },
    [settingsKey],
  );

  // Ids are unique across both lists (one table), so one patch helper serves both.
  const updateRow = useCallback((id: number, patch: Partial<TableLayoutTemplate>) => {
    const apply = (prev: TableLayoutTemplate[]) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
    setMine(apply);
    setSystemViews(apply);
  }, []);
  const removeRow = useCallback((id: number) => {
    const apply = (prev: TableLayoutTemplate[]) => prev.filter((r) => r.id !== id);
    setMine(apply);
    setSystemViews(apply);
  }, []);
  const invalidateRefetch = useCallback(() => {
    // Any write invalidates a refetch that is already in flight.
    refreshGenRef.current += 1;
  }, []);
  const backgroundRefresh = useCallback(() => void refresh(), [refresh]);

  const mutations = useRowMutations({
    updateRow,
    removeRow,
    setError,
    refresh: backgroundRefresh,
    onMutationStart: invalidateRefetch,
  });
  const resetMutations = mutations.reset;

  useEffect(() => {
    if (!isOpen) return;
    resetMutations();
    void refresh(true);
  }, [isOpen, refresh, resetMutations]);

  if (!isOpen) return null;

  return (
    <div className={OVERLAY_CLASS}>
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Manage Views"
        className="bg-panel rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <ModalHeader title="Manage Views" onClose={onClose} dragHandleProps={dragHandleProps} />

        <div className="space-y-6 p-6">
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          {loading ? (
            <p className="py-8 text-center text-gray-600 dark:text-gray-400">Loading Views...</p>
          ) : (
            <>
              <section>
                <h3 className="mb-3 text-lg font-semibold text-heading">My Views</h3>
                {mine.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    You have not saved any Views yet.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {mine.map((template) => (
                      <LayoutRow
                        key={template.id}
                        row={template}
                        caption={<>Updated {formatDate(template.updated_at)}{modeSuffix(template)}</>}
                        mutations={mutations}
                        showShare
                      />
                    ))}
                  </ul>
                )}
              </section>

              {/* Only for a caller the server lets manage System Views — for
                  everyone else the heading would be an empty promise. */}
              {systemViews.length > 0 && (
                <section>
                  <h3 className="mb-3 text-lg font-semibold text-heading">System Views</h3>
                  <ul className="space-y-2">
                    {systemViews.map((view) => (
                      <LayoutRow
                        key={`sys-${view.id}`}
                        row={view}
                        caption={
                          <>Published by {view.user_email} &middot; {formatDate(view.updated_at)}{modeSuffix(view)}</>
                        }
                        mutations={mutations}
                      />
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}

          <div className="flex justify-end pt-2">
            <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
